import moment from 'moment';

import {
  MARKET_CLOSE_NY_TIME,
  MARKET_OPEN_NY_TIME,
  UTC_MOMENT_FORMAT,
} from '../../lib/constants/date-time.constants';
import lang from '../../lib/language';
import { Earning } from '../../lib/models/fundamental/types';
import { EarningsCalendarSearchState, SearchEarningsPayload } from '../../lib/store/fundamental/types';
import {
  formatCurrencyBySymbol,
  getCurrencySymbolByCurrencyCodeOrSymbol,
} from '../../lib/util/DataHelpers';
import { earningsCalendarInitialDate } from '../../lib/util/DateTimeHelpers';

import { getDateRangeByDropdownOption } from '../DesktopCalendar/dateRangeDropdownHelpers';
import { DateRangeDropdownOptionEnum } from '../DesktopCalendar/DateRangePickerWrapper.types';
import { formatEarningsCardDate } from '../UpcomingEarnings/helpers';

export type SymbolFilterOption = { id: string; label: string };

/** Mirrors the host's DateRangeState, which the shared calendar pickers emit. */
export type EarningsDateRange = {
  startDate?: string | Date;
  endDate?: string | Date;
  key?: string;
};

export const groupByDate = (earnings: Earning[]): [string, Earning[]][] => {
  const result = {};
  for (const earning of earnings) {
    if (!result[earning.date]) result[earning.date] = [];
    result[earning.date].push(earning);
  }
  return Object.entries(result);
};

export const formatDate = (date): string => moment(date).format(UTC_MOMENT_FORMAT);

export const formatEarningsRowDateLabel = (date: string): string => {
  const { isToday, day, month, year } = formatEarningsCardDate(date);
  return isToday ? lang.commonDiscoverCalenderEventsToday() : `${month} ${day}, ${year}`;
};

export const formatEarningsCurrency = (value: string, currency: string, symbol: string): string => {
  const currencySymbol = getCurrencySymbolByCurrencyCodeOrSymbol(currency, symbol);

  return formatCurrencyBySymbol(value, currencySymbol);
};

export const getDefaultEarningsSearchDate = (): Date => earningsCalendarInitialDate() || new Date();

export const getDefaultOneMonthRange = (): EarningsDateRange => {
  const { startDate, endDate } = getDateRangeByDropdownOption(
    DateRangeDropdownOptionEnum.OneMonth,
    getDefaultEarningsSearchDate(),
  );

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    key: 'selection',
  };
};

export const createSearchEarningsPayload = (
  startDate: Date | string | undefined,
  endDate: Date | string | undefined,
  page?: number,
  symbols?: string[],
  symbologies?: SearchEarningsPayload['symbologies'],
): SearchEarningsPayload => ({
  startDate: formatDate(startDate || getDefaultEarningsSearchDate()),
  endDate: formatDate(endDate || getDefaultEarningsSearchDate()),
  // The service defaults to date DESC - the calendar shows events chronologically.
  sortAscending: true,
  ...(typeof page === 'number' ? { page } : {}),
  ...(symbols?.length ? { symbols } : {}),
  ...(symbologies?.length ? { symbologies } : {}),
});

// surpriseEps/formattedEps are already currency-formatted (e.g. "$0.05"), so the sign must be
// read off the raw numeric value - Number() on the formatted string always yields NaN.
export const getFormattedSurpiseEpsWithSign = (surpriseEps: string, formattedEps: string): string =>
  (Number(surpriseEps) > 0 ? `+${formattedEps}` : formattedEps);

export const isSearchEarningsQueryFetched = (
  lastFetchedQuery: EarningsCalendarSearchState['lastFetchedQuery'],
  payload: SearchEarningsPayload,
): boolean => {
  if (!lastFetchedQuery) return false;

  const payloadSymbols = payload.symbols ?? [];
  return lastFetchedQuery.startDate === payload.startDate
    && lastFetchedQuery.endDate === payload.endDate
    && lastFetchedQuery.symbols.length === payloadSymbols.length
    && lastFetchedQuery.symbols.every((symbol, i) => symbol === payloadSymbols[i]);
};

export type EarningsCallTimeState = 'reported' | 'afterClose' | 'beforeOpen' | 'notSupplied';

const HOUR_MINUTE_SECOND_PATTERN = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]:[0-5][0-9]$/;
const EARNING_REPORT_STATUS_REPORTED = 'reported';

/**
 * Derived from the raw `time`, never from the formatted `displayTime` - that label is
 * translated, so matching on it silently stops working whenever its wording changes.
 */
export const getEarningsCallTimeState = (
  time: string | undefined,
  reportStatus: string | undefined,
): EarningsCallTimeState => {
  if (reportStatus?.toLowerCase() === EARNING_REPORT_STATUS_REPORTED) return 'reported';
  if (!time || !HOUR_MINUTE_SECOND_PATTERN.test(time)) return 'notSupplied';
  if (parseInt(time, 10) >= parseInt(MARKET_CLOSE_NY_TIME, 10)) return 'afterClose';
  if (parseInt(time, 10) < parseInt(MARKET_OPEN_NY_TIME, 10)) return 'beforeOpen';

  return 'notSupplied';
};
